import { Box, Text } from '@chakra-ui/react';
import { Response } from 'datx-jsonapi';
import { t } from 'i18next';
import { FC, Fragment, useMemo } from 'react';
import { InView } from 'react-intersection-observer';

import { SkeletonText } from '@/components/core/Skeleton/Skeleton';
import { EventSessionEmbed } from '@/components/event/SessionsList/EventSessionEmbed';
import { NoResults } from '@/components/ui/shared/messages/NoResults/NoResults';
import { SearchCategories, SearchGroup } from '@/enums';
import { useQueryParams } from '@/hooks/useQueryParams';
import { useResource, useResourcesInfinite } from '@/lib/datx-jsonapi-react';
import { queryThinEventSession, queryThinEventSessionsByQueryInfinite } from '@/queries/thin-event-sessions';
import { ThinEventSession } from '@/resources/ThinEventSession';
import { extractSWRInfiniteData } from '@/utils/swr/extractSWRInfiniteData';
import { StringParam } from '@/utils/url-query-string';

interface IEventSessionsProps {
  scope?: SearchCategories;
  hideSessions?: boolean;
}

export const EventSessionsEmbed: FC<IEventSessionsProps> = ({ scope, hideSessions }) => {
  const { eventId, eventSessionId, searchGroup, search, trackId, query } = useQueryParams<{
    eventId: string;
    eventSessionId: string;
    searchGroup: string;
    search: string;
    trackId: string;
    query: string;
  }>({
    eventId: StringParam,
    eventSessionId: StringParam,
    searchGroup: StringParam,
    search: StringParam,
    trackId: StringParam,
    query: StringParam,
  });

  const filters: Record<string, string> = {
    event_id: eventId,
    listed: 'true',
    searchable_attribute: searchGroup === 'event_session' ? 'name' : '',
    query: search || query,
    track_id: trackId,
  };

  if (scope === SearchCategories.LECTURES) {
    filters.scope = 'with_lectures';
  }

  if (scope === SearchCategories.POSTERS) {
    filters.poster_only = 'true';
  }

  if (!searchGroup || searchGroup !== 'event_session') {
    delete filters.searchable_attribute;
  }

  const { data: eventSessions, error, size, setSize } = useResourcesInfinite(
    queryThinEventSessionsByQueryInfinite({
      filters,
    }),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  const isSession = searchGroup === SearchGroup.EVENT_SESSION;

  const { items, isReachingEnd, isLoadingMore: isLoadingSessions } = useMemo(
    () => extractSWRInfiniteData<ThinEventSession>(eventSessions as Array<Response<ThinEventSession>>, error, size),
    [eventSessions, error, size]
  );

  const { data: filteredEventSession, error: filteredEventSessionError } = useResource(
    queryThinEventSession(eventSessionId)
  );

  return (
    <Box p={{ base: 2, lg: 8 }}>
      {eventSessionId ? (
        <Fragment>
          {filteredEventSession && <EventSessionEmbed eventSession={filteredEventSession} scope={scope} />}

          {!filteredEventSession && !filteredEventSessionError && (
            <SkeletonText css={{ margin: '24px 32px' }} noOfLines={3} />
          )}
        </Fragment>
      ) : (
        <Fragment>
          {!hideSessions &&
            items.map((session) => <EventSessionEmbed key={session.id} eventSession={session} scope={scope} />)}
          {!items.length && search && !isLoadingSessions && isSession && (
            <Fragment>
              <Text mb={8} mt={14}>
                0 results
              </Text>
              <NoResults
                title={search ? t('common.noResultsForQuery', { query: search }) : t('common.noResults')}
                subtitle={t('eventSearch.noResultsMessage')}
              />
            </Fragment>
          )}
          {isLoadingSessions &&
            !isReachingEnd &&
            Array.from({ length: 10 }, (_, index) => (
              <SkeletonText key={index} css={{ margin: '24px 32px' }} noOfLines={3} />
            ))}

          <InView
            as="span"
            rootMargin="20px 0px 0px 0px"
            onChange={(inView) => {
              if (inView && !error && !isReachingEnd) {
                setSize(size + 1);
              }
            }}
          >
            <span />
          </InView>
        </Fragment>
      )}
    </Box>
  );
};
